import * as React from 'react'
import InformationHeader from 'vendors/components/vendorInformation/InformationHeader'
import InformationForm from 'vendors/components/vendorInformation/InformationForm'
import { useGetVendorDetailsById, useUpdateVendorDetails } from 'vendors/queries/useVendorQuery'
import { useParams, Navigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import LoadingButton from 'proposals/components/LoadingButton'
import { useSnackbar } from 'snackbar/SnackbarContext'
import { useTranslation } from 'react-i18next'

type ValuesType = {
  displayName: string
  websiteUrl: string
  description: string
  language: string
  region: string
  ccEmail: string
}

type VendorInformationErrors = {
  displayName: string
  [key: string]: string
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const useVendorId = () => {
  const { vendorId } = useParams<{ vendorId: string }>()
  return vendorId as string 
}

const VendorManageInformation: React.FC = () => {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()

  const vendorId = useVendorId() 

  const {
    mutate: updateVendor,
    isPending: isUpdating,
    isError: isErrorOnUpdate,
    isSuccess: isUpdateSuccess
  } = useUpdateVendorDetails(vendorId)

  const [values, setValues] = React.useState<ValuesType>({
    displayName: '',
    websiteUrl: '',
    description: '',
    //30.5.2023 - 10583 GLOBALIZATION - hardcoded language and region
    language: 'fi',
    region: 'FI',
    ccEmail: ''
  })

  const { isLoading, isError, data: vendorDetails, isSuccess } = useGetVendorDetailsById(vendorId)

  React.useEffect(() => {
    if (isSuccess) {
      setValues((oldValues) => ({ ...oldValues, ...vendorDetails }))
    }
  }, [vendorDetails, isSuccess])

  const [errors, setErrors] = React.useState<VendorInformationErrors>({
    displayName: ''
  })

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setValues((oldValues) => ({ ...oldValues, [name]: value }))
  }

  const onDecidingRoleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValues({ ...values, [event.target.name]: event.target.checked })
  }

  const onReceiveRequestForProposalsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValues({ ...values, [event.target.name]: event.target.checked })
  }

  const handleBlurValidation = (event: React.FocusEvent<HTMLInputElement>) => {
    const { value, name } = event.target
    const errorMessage = value === '' ? t('required-field') : ''
    setErrors((oldState) => ({ ...oldState, [name]: errorMessage }))
  }

  const handleCcEmailBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { value } = event.target
    const errorMessage = value !== '' && !EMAIL_REGEX.test(value) ? t('invalid-email') : ''
    setErrors((oldState) => ({ ...oldState, ccEmail: errorMessage }))
  }

  const handleUpdateVendor = () => {
    if (errors.displayName !== '') {
      return
    }

    const ccEmail = values.ccEmail ?? ''
    if (ccEmail !== '' && !EMAIL_REGEX.test(ccEmail)) {
      setErrors((oldState) => ({ ...oldState, ccEmail: t('invalid-email') }))
      return
    }

    updateVendor(values)
  }

  React.useEffect(() => {
    if (isUpdateSuccess) {
      showSnackbar(t('information-updated-successfully'))
    }
  }, [showSnackbar, isUpdateSuccess, t])

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (isError || isErrorOnUpdate) {
    return <Navigate to="/error" replace />
  }

  return (
    <>
      <InformationHeader
        companyName={vendorDetails?.name ? vendorDetails?.name : ''}
        businessId={vendorDetails?.businessId ? vendorDetails.businessId : ''}
      />
      <InformationForm
        values={values}
        onChange={onChange}
        onDecidingRoleChange={onDecidingRoleChange}
        onReceiveRequestForProposalsChange={onReceiveRequestForProposalsChange}
        onBlur={handleBlurValidation}
        onCcEmailBlur={handleCcEmailBlur}
        errors={errors}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2 }}>
        <LoadingButton
          disabled={errors.displayName !== '' || values.displayName === '' || Boolean(errors.ccEmail)}
          onClick={handleUpdateVendor}
          isLoading={isUpdating}
        >
          {t('save')}
        </LoadingButton>
      </Box>
    </>
  )
}

export default VendorManageInformation
